import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { InjuryProps } from './InjuryForm'
import BaseFormContainer from '../../../components/Form/FormContainer'
import BaseDataPanel from './BaseDataPanel'
import MedicalAssistancePanel from './MedicalAssistancePanel'
import TrafficAccidentPanel from './TrafficAccidentPanel'
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, makeStyles, shorthands, tokens } from '@fluentui/react-components'
import OccupationalAccidentPanel from './OccupationalAccidentPanel'
import UnfitForWorkPanel from './UnfitForWorkPanel'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import { FieldProps } from '@fluentui/react-field'

const useStyles = makeStyles({
    container: {
        display: 'grid',
        gridTemplateRows: 'auto min-content',
        ...shorthands.gap(tokens.spacingVerticalL),
    },
    accordionPanel: shorthands.margin(0),
    accordionHeader: {
        '> button': shorthands.padding(0),
    },
    formRow: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingVerticalS),
    },
    input: { minWidth: 0, width: '100%', boxSizing: 'border-box', '> input': { textAlign: 'right', width: '100%' } },
    field: {
        alignItems: 'center',
        '> label': {
            ...shorthands.overflow('hidden'),
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
    },
})

export type InjuryPanelProps = InjuryProps & {
    orientation: FieldProps['orientation']
    styles: Record<'container' | 'input' | 'field' | 'formRow', string>
}

export default function FormContainer(): ReactElement {
    const styles = useStyles()
    const { isViewport, viewport } = useViewport()
    const orientation = useMemo(() => (isViewport('>=', Viewport.Large) && isViewport('!=', Viewport.XL) ? 'horizontal' : 'vertical'), [viewport])

    return (
        <BaseFormContainer>
            <div className={styles.container}>
                <Accordion multiple collapsible defaultOpenItems={['base', 'medicalAssistance']}>
                    <AccordionItem value="base">
                        <AccordionHeader className={styles.accordionHeader}>Stamgegevens</AccordionHeader>
                        <AccordionPanel className={styles.accordionPanel}>
                            <BaseDataPanel styles={styles} orientation={orientation} />
                        </AccordionPanel>
                    </AccordionItem>
                    <AccordionItem value="medicalAssistance">
                        <AccordionHeader className={styles.accordionHeader}>Medische begeleiding</AccordionHeader>
                        <AccordionPanel className={styles.accordionPanel}>
                            <MedicalAssistancePanel styles={styles} orientation={orientation} />
                        </AccordionPanel>
                    </AccordionItem>
                    <AccordionItem value="trafficAccident">
                        <AccordionHeader className={styles.accordionHeader}>Verkeersongeval</AccordionHeader>
                        <AccordionPanel className={styles.accordionPanel}>
                            <TrafficAccidentPanel styles={styles} orientation={orientation} />
                        </AccordionPanel>
                    </AccordionItem>
                    <AccordionItem value="occupationalAccident">
                        <AccordionHeader className={styles.accordionHeader}>Arbeidsongeval</AccordionHeader>
                        <AccordionPanel className={styles.accordionPanel}>
                            <OccupationalAccidentPanel styles={styles} orientation={orientation} />
                        </AccordionPanel>
                    </AccordionItem>
                    <AccordionItem value="unfitForWorkPanel">
                        <AccordionHeader className={styles.accordionHeader}>Arbeidsongeschiktheid</AccordionHeader>
                        <AccordionPanel className={styles.accordionPanel}>
                            <UnfitForWorkPanel styles={styles} orientation={orientation} />
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            </div>
        </BaseFormContainer>
    )
}
